/* eslint-disable */
/**
 * Validation and sanitization utilities for Spray Calculator
 * Handles French locale input (comma as decimal separator) and product row validation
 */

/**
 * Sanitize dose input value for French locale
 * - Allows numbers, comma, dot, and minus sign
 * - Converts comma to dot for storage (numeric operations)
 * - Returns null for empty or invalid input
 *
 * @param {string|number} value - Raw input value from user
 * @returns {number|null} Parsed numeric value or null
 */
export function sanitizeDoseValue(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const stringValue = String(value);

  // Replace comma with dot for numeric parsing
  const normalized = stringValue.replace(',', '.');

  // Remove any characters that aren't numbers, dots, or minus
  const cleaned = normalized.replace(/[^0-9.\-]/g, '');

  if (!cleaned || cleaned === '-' || cleaned === '.') {
    return null;
  }

  const parsed = parseFloat(cleaned);

  if (isNaN(parsed)) {
    return null;
  }

  return parsed;
}

/**
 * Sanitize dose input for display and storage
 * Follows the same pattern as the Risk Calculator's sanitizeDoseInput
 *
 * @param {string} inputValue - Raw input value from user
 * @returns {Object} { displayValue: string, storedValue: string }
 */
export function sanitizeDoseInput(inputValue) {
  // Sanitize input (allow numbers, comma, dot, minus)
  let sanitized = inputValue.replace(/[^0-9,.\-]/g, '');

  // Allow minus only at start
  const hasMinus = sanitized.startsWith('-');
  sanitized = sanitized.replace(/-/g, '');
  if (hasMinus) sanitized = '-' + sanitized;

  // Replace dot with comma for display
  sanitized = sanitized.replace(/\./g, ',');

  // Only allow one comma
  const parts = sanitized.split(',');
  if (parts.length > 2) {
    sanitized = parts[0] + ',' + parts.slice(1).join('');
  }

  // Convert comma to dot for storage
  const storedValue = sanitized.replace(',', '.');

  return {
    displayValue: sanitized,
    storedValue,
  };
}

/**
 * Format dose for display (replace dot with comma)
 * French locale uses comma as decimal separator
 *
 * @param {string|number} dose - Dose value with dot as decimal separator
 * @returns {string} Formatted dose with comma as decimal separator
 */
export function formatDoseDisplay(dose) {
  if (!dose || dose === '') return '';
  return String(dose).replace('.', ',');
}

/**
 * Check if a product row has valid data for submission
 *
 * @param {Object} row - Product row object
 * @param {string} row.productId - Product ID
 * @param {string|number} row.dose - Applied dose
 * @param {string} row.unitId - Unit ID
 * @returns {boolean} True if row has all required fields with valid values
 */
export function isValidProduct(row) {
  if (!row) return false;

  const hasProduct = row.productId && row.productId !== '';
  const hasDose =
    row.dose !== '' &&
    row.dose !== null &&
    row.dose !== undefined &&
    parseFloat(row.dose) > 0;
  const hasUnit = row.unitId && row.unitId !== '';

  return hasProduct && hasDose && hasUnit;
}

/**
 * Check if a product row is empty (no product selected)
 *
 * @param {Object} row - Product row object
 * @returns {boolean} True if row has no product selected
 */
export function isRowEmpty(row) {
  return !row.productId || row.productId === '';
}

/**
 * Check if a product row is complete (has product, dose, and unit)
 * Used to determine when to auto-add a new empty row
 *
 * @param {Object} row - Product row object
 * @returns {boolean} True if row has all fields filled
 */
export function isRowComplete(row) {
  return (
    row.productId &&
    row.productId !== '' &&
    row.dose &&
    row.dose !== '' &&
    parseFloat(row.dose) > 0 &&
    row.unitId &&
    row.unitId !== ''
  );
}

/**
 * Filter out empty rows from the product list
 * Returns only rows that have a product selected
 *
 * @param {Array} rows - Array of product row objects
 * @returns {Array} Filtered array with only rows that have products
 */
export function filterValidRows(rows) {
  return rows.filter((row) => row.productId && row.productId !== '');
}
